import cn from "classnames"
import { debounce } from "lodash-es"
import React, { useState, useCallback, useRef, useMemo, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import type SignText from "../app/objects/sign_text"
import Blinker from "@/common/components/blinker"
import Scrollable from "@/common/components/scrollable"
import Slider from "@/common/components/slider"
import { isTablet, metaNameSign } from "@/common/util"
import Icons from "@/images/signomatic/objects"
import ShapeIcons from "@/images/signomatic/shapes"
import Sign from "@/signomatic/app/sign"
import { currentMinText } from "@/signomatic/app/sign_validator"
import ColorPicker from "@/signomatic/components/color_picker"
import Hint from "@/signomatic/components/hint"
import ObjectBorderPicker from "@/signomatic/components/object_border_picker"
import Toolbox from "@/signomatic/components/toolbox"
import { type ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"
import ColorUtil from "@/signomatic/util/colors"
import Fonts from "@/signomatic/util/fonts"
import { ChevronLeftLarge, ChevronRightLarge, TrashOutline, DuplicateOutline, PlusLarge } from "@/skylticons"
import TextLargeIcon from "@/skylticons/text_size_large"
import TextSmallIcon from "@/skylticons/text_size_small"

interface FontPreview {
  width: number
  height: number
  data: string
}

const fontPreviews: Record<string, FontPreview | undefined> = {}

interface FontItemProps {
  label: string
  value: string
  custom: boolean
  selected?: boolean
  onClick?: (value: string) => void
}

const FontItem: React.FC<FontItemProps> = ({ label, value, selected, custom, onClick }) => {
  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(value)
    }
  }, [onClick, value])

  let preview = fontPreviews[label]

  if (!preview) {
    const font = Fonts.getOpentypeFont(label === "Braille (SE 6p)" ? "Arial" : label, "normal")

    if (font) {
      const path = font.getPath(label, 0, 19, 19)
      const box = path.getBoundingBox()
      preview = fontPreviews[label] = {
        width: box.x2,
        height: box.y2,
        data: path.toPathData(2),
      }
    }
  }

  return (
    <div className={cn("font-item flex", label.replace(new RegExp(" ", "g"), ""), { selected })} onClick={handleClick}>
      {custom && <span className="mr-2">(Admin only)</span>}
      {preview ? (
        <svg width={preview.width} height={preview.height}>
          <path d={preview.data} />
        </svg>
      ) : (
        label
      )}
    </div>
  )
}

interface FontGroupProps {
  label: string
  items: FontItemProps[]
  onClick?: (value: string) => void
  classes?: string
}

const FontGroup: React.FC<FontGroupProps> = ({ label, items, onClick, classes }) => {
  return (
    <div className={cn("font-group", classes)}>
      <div className="paragraph-2xs font-header">{label}</div>
      {items.map((item, i) => (
        <FontItem key={i} onClick={onClick} {...item} />
      ))}
    </div>
  )
}

interface ButtonProps {
  name: string
  label?: string
  active?: boolean
  disabled?: boolean
  onClick: (e: React.FormEvent) => void
  classes?: string
  iconOnly?: boolean
}

const Button: React.FC<ButtonProps> = ({ name, active, label, onClick, disabled, classes, iconOnly }) => {
  const iconClasses = iconOnly
    ? `button button-medium icon-only full-width mb-0 ${active ? "button-secondary" : "button-subtle"}`
    : ""

  return (
    <button className={cn(name, classes, iconClasses, { active, disabled })} onClick={onClick} type="button">
      {iconOnly ? (
        Icons[name] ? (
          React.createElement(Icons[name])
        ) : (
          ""
        )
      ) : (
        <>
          <div className="image">{Icons[name] ? React.createElement(Icons[name]) : ""}</div>
          {label && <div className="name paragraph-2xs center mb-0">{label}</div>}
        </>
      )}
    </button>
  )
}

interface ToggleButtonProps {
  name: string
  label?: string
  disabled?: boolean
  activeObject: SignObjectState
  onUpdate: (value: Partial<SignTextState>) => void
}

const ToggleButton: React.FC<ToggleButtonProps> = ({ name, disabled, label, activeObject, onUpdate }) => {
  const active: boolean = activeObject[name]

  const onChange = () => {
    if (disabled) {
      return
    }

    const state: Partial<SignTextState> = {}
    state[name] = !active
    onUpdate(state)
  }

  return (
    <Button
      name={name}
      active={active}
      onClick={onChange}
      label={label}
      disabled={disabled}
      classes="object_button no-hover-mobile no-focus"
      iconOnly
    />
  )
}

interface TextToolboxStateProps {
  data: SignData | null
  signType: string
  subToolbox: string | null
  signState: SignState | null
  activeObject: SignObjectState | null
}

interface TextToolboxDispatchProps {
  setToolbox: typeof actions.setToolbox
  setSubToolbox: typeof actions.setSubToolbox
  setSubTarget: typeof actions.setSubTarget
  setToolboxHeight: typeof actions.setToolboxHeight
}

interface ToolboxBaseProps {
  setActiveTab: (tab: string) => void
  setToolboxHeight: (height: number) => void
  sign: Sign
}

const BorderTab: React.FC<ToolboxBaseProps> = ({ sign, setActiveTab, setToolboxHeight }) => {
  const { t } = useTranslation()

  return (
    <>
      <TopBar
        backTo="active-text"
        header={t("toolbox.border")}
        setActiveTab={setActiveTab}
        sign={sign}
        setToolboxHeight={setToolboxHeight}
      />
      {window.innerWidth <= 1199 ? (
        <div className="object-border-picker">
          <ObjectBorderPicker sign={sign} />
        </div>
      ) : (
        <Scrollable>
          <div className="object-border-picker">
            <ObjectBorderPicker sign={sign} />
          </div>
        </Scrollable>
      )}
    </>
  )
}

type TopBarProps = ToolboxBaseProps & {
  backTo: string
  header: string
}

const TopBar: React.FC<TopBarProps> = ({ backTo, header, setActiveTab, sign, setToolboxHeight }) => {
  const { t } = useTranslation()
  const handleBack = () => {
    setActiveTab(backTo)

    if (backTo === "all-objects") {
      sign.setObjectActive(null)
    }

    if (isTablet()) {
      setToolboxHeight(288)
    }
  }

  return (
    <div className="ml-3 flex h-12 items-center">
      <button className="toolbox-back button button-subtle icon-left mb-0 px-2" onClick={handleBack} type="button">
        <ChevronLeftLarge className="!mr-1 h-6 w-6" />
        <span className="text-sm font-semibold">{t("toolbox.back")}</span>
      </button>
      <span className="absolute left-1/2 -translate-x-1/2 text-base font-semibold">{header}</span>
    </div>
  )
}

interface HintsProps {
  missingGlyphs: boolean
  emojiUsed: boolean
  setToolbox: typeof actions.setToolbox
  signType: string
  text: string
}

const Hints: React.FC<HintsProps> = ({ missingGlyphs, emojiUsed, setToolbox, signType, text }) => {
  if (missingGlyphs) {
    return <Hint show type="errormessage" translation="toolbox.hint_missing_glyph" />
  }

  if (emojiUsed) {
    const link = (
      <a
        onClick={e => {
          e.preventDefault()
          setToolbox("image")
        }}
      >
        image archive
      </a>
    )

    return <Hint show type="tip" translation="toolbox.hint_emoji" components={{ a: link }} />
  }

  const showHint = metaNameSign(signType) || text.includes("\n")
  return <Hint show={showHint} type="tip" translation="toolbox.hint_multiple_objects" />
}

type AllObjectsTabProps = HintsProps & {
  data: SignData | null
  signState: SignState | null
  sign: Sign
  setActiveTab: (tab: string) => void
}

const AllObjectsTab: React.FC<AllObjectsTabProps> = ({
  data,
  signState,
  sign,
  setActiveTab,
  missingGlyphs,
  emojiUsed,
  setToolbox,
  signType,
  text,
}) => {
  const { t } = useTranslation()

  if (!data || !signState) {
    return
  }

  const objects = (signState ? signState.objects : []).filter(Sign.isText)

  return (
    <Scrollable>
      <div className="all-objects">
        <div className="objects">
          <h5>{t("toolbox.all_texts")}</h5>
          {!(data.single_object && objects.length > 0) && (
            <button
              id="btn_add_text"
              className="button button-secondary full-width"
              disabled={!sign.canAddTextObject}
              onClick={() => {
                setActiveTab("active-text")
                sign.createObject("text").then(text => {
                  if (text) {
                    sign.setObjectActive(text.state)
                  }
                })
              }}
              type="button"
            >
              <PlusLarge />
              {t("toolbox.add_new_text")}
            </button>
          )}
          <div className="divider"></div>
          {objects.map((obj, i) => {
            if (signState.doubleSided && obj.side !== signState.side) {
              return null
            }

            return (
              <div
                key={i}
                className="object-button"
                onClick={() => {
                  sign.setObjectActive(obj)
                  setActiveTab("active-text")
                }}
              >
                <div className="paragraph-2xs">{t("toolbox.text_object") + " " + (i + 1)}</div>
                <div
                  className={cn("paragraph sign-text-value data-hj-suppress mb-0", {
                    bold: obj.bold,
                    placeholder: !obj.text,
                  })}
                >
                  {obj.text || t("signomatic.your_text_here")}
                </div>
              </div>
            )
          })}
        </div>
        <Hints
          missingGlyphs={missingGlyphs}
          emojiUsed={emojiUsed}
          setToolbox={setToolbox}
          signType={signType}
          text={text}
        />
      </div>
    </Scrollable>
  )
}

type ActiveTextTabProps = ToolboxBaseProps & {
  activeObject: SignObjectState | null
  data: SignData | null
  signState: SignState | null
  signType: string
  updateObject: (value: Partial<SignTextState>) => void
}

const ActiveTextTab: React.FC<ActiveTextTabProps> = ({
  activeObject,
  data,
  sign,
  signState,
  setActiveTab,
  setToolboxHeight,
  signType,
  updateObject,
}) => {
  const { t } = useTranslation()

  if (!activeObject || !Sign.isText(activeObject) || !data) {
    return null
  }

  const objects = (signState ? signState.objects : []).filter(o => Sign.isText(o))
  const index = objects.indexOf(activeObject)

  const allowedStyles = Fonts.allowedStyles(activeObject.font, sign.fontdata)
  const customFonts = sign.fontdata.sets["custom"]
  const isCustom = customFonts.includes(activeObject.font)
  const currentFont = isCustom ? `(Admin only) ${activeObject.font}` : activeObject.font

  return (
    <>
      <TopBar
        backTo="all-objects"
        header={t("toolbox.text_object") + " " + (index + 1)}
        setActiveTab={setActiveTab}
        setToolboxHeight={setToolboxHeight}
        sign={sign}
      />
      <Scrollable>
        <div className="object-settings-content">
          <div
            className="object-option-row add-text"
            onClick={() => {
              setActiveTab("text-content")
              if (isTablet()) {
                // 186 (heigth of text content edit toolbox) - 186 (offset to get the sign at about the correct height)
                setToolboxHeight(0)
              }
            }}
          >
            <span className="left paragraph paragraph-sm bold mb-0" style={{ whiteSpace: "nowrap", paddingRight: 4 }}>
              {t("toolbox.change_text")}
            </span>
            <span className="right">
              {activeObject.text === "" ? (
                <span className="enter-text-here">
                  <span className="paragraph paragraph-sm mb-0">{t("signomatic.your_text_here")}</span>
                  <Blinker />
                </span>
              ) : (
                <span className="paragraph paragraph-sm sign-text-value data-hj-suppress mb-0">
                  {activeObject.text}
                </span>
              )}
              <span className="chevron">
                <ChevronRightLarge />
              </span>
            </span>
          </div>
          {!sign.isCableTag && (
            <>
              <div className="textinput-wrapper">
                <div className="toggle-buttons">
                  <ToggleButton
                    name="bold"
                    activeObject={activeObject}
                    onUpdate={updateObject}
                    disabled={!allowedStyles.bold || (activeObject.italic && !allowedStyles.boldItalic)}
                  />
                  <ToggleButton
                    name="italic"
                    activeObject={activeObject}
                    onUpdate={updateObject}
                    disabled={!allowedStyles.italic || (activeObject.bold && !allowedStyles.boldItalic)}
                  />
                  <ToggleButton
                    name="underlined"
                    activeObject={activeObject}
                    onUpdate={updateObject}
                    disabled={Fonts.disableUnderline(activeObject.font, signType)}
                  />
                  <Button
                    name="justify_left"
                    active={activeObject.justify === "left"}
                    onClick={() => updateObject({ justify: "left" })}
                    classes="object_button no-hover-mobile no-focus"
                    iconOnly
                  />
                  <Button
                    name="justify_center"
                    active={activeObject.justify === "center"}
                    onClick={() => updateObject({ justify: "center" })}
                    classes="object_button no-hover-mobile no-focus"
                    iconOnly
                  />
                  <Button
                    name="justify_right"
                    active={activeObject.justify === "right"}
                    onClick={() => updateObject({ justify: "right" })}
                    classes="object_button no-hover-mobile no-focus"
                    iconOnly
                  />
                </div>
              </div>

              <div className="object-option-row font-name" onClick={() => setActiveTab("font-name")}>
                <span className="left paragraph paragraph-sm bold mb-0">{t("toolbox.font")}</span>
                <span className="right">
                  <span className="paragraph paragraph-sm font-name mb-0">{currentFont}</span>
                  <span className="chevron">
                    <ChevronRightLarge />
                  </span>
                </span>
              </div>
              <div className="object-option-row font-size" onClick={() => setActiveTab("font-size")}>
                <span className="left paragraph paragraph-sm bold mb-0">{t("toolbox.text_size")}</span>
                <span className="right">
                  <span className="paragraph paragraph-sm mb-0">{activeObject.size}</span>
                  <span className="chevron">
                    <ChevronRightLarge />
                  </span>
                </span>
              </div>
              {(data.multicolor || data.transparent) && (
                <div className="object-option-row color" onClick={() => setActiveTab("color")}>
                  <span className="left paragraph paragraph-sm bold mb-0">{t("toolbox.color")}</span>
                  <span className="right">
                    <span className="paragraph paragraph-sm mb-0">
                      {t(`background_colors.${activeObject.color}`, { defaultValue: activeObject.color })}
                    </span>
                    <span
                      className={cn("color-swatch", ColorUtil.colorClass(activeObject.color))}
                      style={{ backgroundColor: activeObject.color }}
                    ></span>
                    <span className="chevron">
                      <ChevronRightLarge />
                    </span>
                  </span>
                </div>
              )}
              <div className="object-option-row alignment" onClick={() => setActiveTab("alignments")}>
                <span className="left paragraph paragraph-sm bold mb-0">{t("toolbox.alignment")}</span>
                <span className="right">
                  <span className="chevron">
                    <ChevronRightLarge />
                  </span>
                </span>
              </div>
              {data.object_borders && (
                <div className="object-option-row border-type" onClick={() => setActiveTab("border")}>
                  <span className="left paragraph paragraph-sm bold mb-0">{t("toolbox.border")}</span>
                  <span className="right">
                    <span className="paragraph paragraph-sm mb-0">
                      {activeObject.border ? t(`shape_type.${activeObject.border.shape}`) : t("shape_type.none")}
                    </span>
                    <span className="border-icon">
                      {activeObject.border
                        ? React.createElement(ShapeIcons[activeObject.border.shape])
                        : React.createElement(ShapeIcons["none"])}
                    </span>
                    <span className="chevron">
                      <ChevronRightLarge />
                    </span>
                  </span>
                </div>
              )}
            </>
          )}
        </div>
        <div className="actions">
          <div className="paragraph paragraph-sm bold mb-0">{t("toolbox.actions")}</div>
          <span className="buttons">
            <button
              className="duplicate button button-secondary mb-0"
              onClick={() => {
                sign.duplicateObject(activeObject, sign.centerX, sign.centerY)
              }}
              type="button"
            >
              <DuplicateOutline />
              {t("misc.duplicate")}
            </button>
            <button
              className="delete button button-secondary button-error mb-0"
              onClick={e => {
                e.stopPropagation()
                sign.deleteObject(activeObject)
              }}
              type="button"
            >
              <TrashOutline />
              {t("misc.delete")}
            </button>
          </span>
        </div>
      </Scrollable>
    </>
  )
}

type TextContentTabProps = ToolboxBaseProps & {
  text: string
  updateText: (e: React.ChangeEvent<HTMLTextAreaElement>) => Promise<void>
  onFocus: (e?: React.FocusEvent<HTMLTextAreaElement, Element> | undefined) => void
  textboxRef: React.RefObject<HTMLTextAreaElement>
}

const TextContentTab: React.FC<TextContentTabProps> = ({
  setActiveTab,
  setToolboxHeight,
  sign,
  text,
  updateText,
  onFocus,
  textboxRef,
}) => {
  const { t } = useTranslation()

  return (
    <>
      <TopBar
        backTo="active-text"
        header={t("toolbox.change_text")}
        setActiveTab={setActiveTab}
        setToolboxHeight={setToolboxHeight}
        sign={sign}
      />
      <div className="text-edit">
        <textarea
          name="sign_text"
          id="sign_text"
          className="mousetrap data-hj-suppress"
          placeholder={t("signomatic.your_text_here")}
          value={text}
          onChange={updateText}
          onFocus={onFocus}
          ref={textboxRef}
          autoFocus
        />
      </div>
    </>
  )
}

type FontNameTabProps = ToolboxBaseProps & {
  activeObject: SignObjectState | null
  data: SignData | null
  signType: string
  updateObject: (value: Partial<SignTextState>) => void
}

const FontNameTab: React.FC<FontNameTabProps> = ({
  sign,
  activeObject,
  data,
  signType,
  setActiveTab,
  setToolboxHeight,
  updateObject,
}) => {
  const { t } = useTranslation()

  const customFonts = sign.fontdata.sets["custom"]

  if (!activeObject || !data) {
    return
  }

  const active: FontItemProps = {
    label: activeObject.font,
    value: activeObject.font.toLowerCase(),
    custom: customFonts.includes(activeObject.font),
    selected: true,
  }

  const fontSet = Fonts.enabledFonts(signType, sign.fontdata, sign.data.font_set || "")

  const fonts: FontItemProps[] =
    fontSet.map(font => ({
      label: font,
      value: font,
      selected: font.toLowerCase() === activeObject.font.toLowerCase(),
      custom: customFonts.includes(font),
    })) || []

  return (
    <>
      <TopBar
        backTo="active-text"
        header={t("toolbox.font")}
        setActiveTab={setActiveTab}
        setToolboxHeight={setToolboxHeight}
        sign={sign}
      />
      <div className="font-name-tab flex flex-1">
        <div className="items flex flex-1">
          <Scrollable>
            <FontGroup label={t("toolbox.selected_font")} items={[active]} />
            <FontGroup
              label={t("toolbox.all_fonts")}
              items={fonts}
              onClick={value => updateObject({ font: value })}
              classes="all-fonts"
            />
          </Scrollable>
        </div>
      </div>
    </>
  )
}

type FontSizeTabProps = ToolboxBaseProps & {
  activeObject: SignObjectState | null
  data: SignData | null
  signState: SignState | null
  updateObject: (value: Partial<SignTextState>) => void
  size: string
}

const FontSizeTab: React.FC<FontSizeTabProps> = ({
  data,
  activeObject,
  setActiveTab,
  setToolboxHeight,
  sign,
  updateObject,
  signState,
  size,
}) => {
  const { t } = useTranslation()

  if (!signState || !data || !activeObject) {
    return
  }

  const min = currentMinText(data, signState, activeObject.font)

  return (
    <>
      <TopBar
        backTo="active-text"
        header={t("toolbox.text_size")}
        setActiveTab={setActiveTab}
        setToolboxHeight={setToolboxHeight}
        sign={sign}
      />
      <div className="font-size-tab">
        <div className="object-size">
          <div className="items">
            <Slider
              inputId="font_size"
              inputName="font_size_range"
              label={t("toolbox.text_size")}
              ariaLabelInput={t("toolbox.text_size_input")}
              min={min}
              max={Math.min(data.max_size.h, signState.height)}
              rangeMinIcon={TextSmallIcon}
              rangeMaxIcon={TextLargeIcon}
              value={parseFloat(size)}
              onChange={value => updateObject({ size: value })}
            />
          </div>
        </div>
      </div>
    </>
  )
}

type ColorTabProps = ToolboxBaseProps & {
  setSubTarget: (target: string) => void
}

const ColorTab: React.FC<ColorTabProps> = ({ setActiveTab, setSubTarget, setToolboxHeight, sign }) => {
  const { t } = useTranslation()

  useEffect(() => {
    setSubTarget("object")
  }, [setSubTarget])

  return (
    <>
      <TopBar
        backTo="active-text"
        header={t("toolbox.text_colour")}
        setActiveTab={setActiveTab}
        setToolboxHeight={setToolboxHeight}
        sign={sign}
      />
      <div className="object-color-picker">
        <ColorPicker sign={sign} />
      </div>
    </>
  )
}

type AlignmentTabProps = ToolboxBaseProps & {
  activeObject: SignObjectState | null
  data: SignData | null
  updateObject: (value: Partial<SignTextState>) => void
}

const AlignmentTab: React.FC<AlignmentTabProps> = ({
  data,
  activeObject,
  setActiveTab,
  setToolboxHeight,
  sign,
  updateObject,
}) => {
  const { t } = useTranslation()

  if (!data || !activeObject) {
    return
  }

  const showReversed = data.transparent || data.name == "sticker"

  const toggleMirrored = () => {
    if (!activeObject) {
      return
    }

    updateObject({ mirrored: !activeObject.mirrored })
  }

  return (
    <>
      <TopBar
        backTo="active-text"
        header={t("toolbox.alignment")}
        setActiveTab={setActiveTab}
        setToolboxHeight={setToolboxHeight}
        sign={sign}
      />
      <div className="object-alignements">
        <div className="center-buttons">
          <Button
            name="align_center"
            active={activeObject.top === 0 && activeObject.left === 0}
            onClick={() => updateObject({ top: 0, left: 0 })}
            label={t("toolbox.center_center")}
            classes="alignement-button"
          />
          <Button
            name="align_vertical"
            active={activeObject.left !== 0 && activeObject.top === 0}
            onClick={() => updateObject({ top: 0 })}
            label={t("toolbox.center_vertical")}
            classes="alignement-button"
          />
          <Button
            name="align_horizontal"
            active={activeObject.left === 0 && activeObject.top !== 0}
            onClick={() => updateObject({ left: 0 })}
            label={t("toolbox.center_horizontal")}
            classes="alignement-button"
          />
        </div>
        <div className="center-buttons">
          <Button
            name="arrange_front"
            onClick={() => sign.arrangeObject(activeObject, "front")}
            label={t("toolbox.arrange_front")}
            classes="alignement-button"
          />
          <Button
            name="arrange_forward"
            onClick={() => sign.arrangeObject(activeObject, "forward")}
            label={t("toolbox.arrange_forward")}
            classes="alignement-button"
          />
          <Button
            name="arrange_backward"
            onClick={() => sign.arrangeObject(activeObject, "backward")}
            label={t("toolbox.arrange_backward")}
            classes="alignement-button"
          />
          {showReversed && (
            <Button
              name="mirrored"
              active={activeObject.mirrored}
              onClick={toggleMirrored}
              label={t("toolbox.reversed")}
              classes="alignement-button"
            />
          )}
        </div>
      </div>
    </>
  )
}

type TextToolboxProps = TextToolboxStateProps & TextToolboxDispatchProps & ToolboxStripItemProps

const TextToolbox: React.FC<TextToolboxProps> = ({
  data,
  signType,
  setToolbox,
  setToolboxHeight,
  setSubTarget,
  signState,
  activeObject,
  sign,
}) => {
  const textboxRef = useRef<HTMLTextAreaElement>(null)
  const previousObject = useRef<symbol | null>(null)
  const [emojiUsed, setEmojiUsed] = useState(false)
  const [missingGlyphs, setMissingGlyphs] = useState(false)

  const [size, setSize] = useState(() => {
    if (activeObject && Sign.isText(activeObject)) {
      return activeObject.size.toFixed(1).toString()
    }
    return "0"
  })

  const [text, setText] = useState(() => {
    if (activeObject && Sign.isText(activeObject)) {
      return activeObject.text
    }
    return ""
  })

  const [activeTab, setActiveTab] = useState(activeObject && Sign.isText(activeObject) ? "active-text" : "all-objects")

  const handleEmojiUsed = useCallback(() => {
    setEmojiUsed(true)
  }, [])

  const updateObject = (state: Partial<SignTextState>) => {
    sign.updateObjectState(state)
  }

  const updateText = async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let value = e.target.value
    const textObj = sign.activeObject as SignText

    if (textObj.isPlaceholder) {
      value = value.replace(textObj.placeholder, "").trim()
      textObj.opacity = 1
      textObj.isPlaceholder = false
      textObj.placeholder = ""
    }

    setText(value)
    updateObject({ text: value })
  }

  const debouncedUpdateGlyphsPresent = useMemo(
    () =>
      debounce(
        (present: boolean) => {
          setMissingGlyphs(!present)
        },
        500,
        { leading: false }
      ),
    []
  )

  const exitEditing = useCallback(
    (e?: React.FocusEvent<HTMLTextAreaElement>) => {
      sign.exitInlineEditing()

      if (e) {
        const textarea = e.target
        textarea.selectionStart = textarea.selectionEnd = textarea.value.length
      }
    },
    [sign]
  )

  const selectedStart = (e?: React.FocusEvent<HTMLTextAreaElement>) => {
    sign.exitInlineEditing()

    if (e) {
      const textarea = e.target
      textarea.selectionStart = textarea.selectionEnd = 0
    }
  }

  const onFocus = (e?: React.FocusEvent<HTMLTextAreaElement>) => {
    const textObj = sign.activeObject as SignText

    return textObj.isPlaceholder ? selectedStart(e) : exitEditing(e)
  }

  const updateObjectFromSignState = useCallback(
    (state: SignObjectState | null) => {
      if (!state) return

      if (activeTab === "all-objects" && Sign.isText(state)) {
        setActiveTab("active-text")
      }

      if (Sign.isText(state)) {
        const newSize = state.size.toFixed(1).toString()
        const newText = state.text

        setSize(prev => (prev === newSize ? prev : newSize))
        setText(prev => (prev === newText ? prev : newText))
      }
    },
    [activeTab]
  )

  useEffect(() => {
    sign.onActiveChanged.subscribe(updateObjectFromSignState)
    sign.onEmojiUsed.subscribe(handleEmojiUsed)
    sign.onGlyphsPresent.subscribe(debouncedUpdateGlyphsPresent)

    exitEditing()

    return () => {
      sign.onActiveChanged.unsubscribe(updateObjectFromSignState)
      sign.onEmojiUsed.unsubscribe(handleEmojiUsed)
      sign.onGlyphsPresent.unsubscribe(debouncedUpdateGlyphsPresent)
      debouncedUpdateGlyphsPresent.cancel()
    }
  }, [sign, debouncedUpdateGlyphsPresent, exitEditing, updateObjectFromSignState, handleEmojiUsed])

  useEffect(() => {
    if (activeObject && activeObject.identity !== previousObject.current && textboxRef.current) {
      textboxRef.current.focus()
    }

    previousObject.current = activeObject ? activeObject.identity : null
  }, [activeObject])

  const activeTabComponent = () => {
    if (activeTab === "all-objects" || !activeObject) {
      return (
        <AllObjectsTab
          data={data}
          signState={signState}
          sign={sign}
          setActiveTab={setActiveTab}
          missingGlyphs={missingGlyphs}
          emojiUsed={emojiUsed}
          setToolbox={setToolbox}
          signType={signType}
          text={text}
        />
      )
    } else if (activeTab === "active-text") {
      return (
        <ActiveTextTab
          activeObject={activeObject}
          data={data}
          sign={sign}
          signState={signState}
          setActiveTab={setActiveTab}
          setToolboxHeight={setToolboxHeight}
          signType={signType}
          updateObject={updateObject}
        />
      )
    } else if (activeTab === "text-content") {
      return (
        <TextContentTab
          setActiveTab={setActiveTab}
          setToolboxHeight={setToolboxHeight}
          sign={sign}
          text={text}
          updateText={updateText}
          onFocus={onFocus}
          textboxRef={textboxRef}
        />
      )
    } else if (activeTab === "font-name") {
      return (
        <FontNameTab
          activeObject={activeObject}
          data={data}
          sign={sign}
          setActiveTab={setActiveTab}
          setToolboxHeight={setToolboxHeight}
          signType={signType}
          updateObject={updateObject}
        />
      )
    } else if (activeTab === "font-size") {
      return (
        <FontSizeTab
          activeObject={activeObject}
          data={data}
          sign={sign}
          signState={signState}
          setActiveTab={setActiveTab}
          setToolboxHeight={setToolboxHeight}
          size={size}
          updateObject={updateObject}
        />
      )
    } else if (activeTab === "color") {
      return (
        <ColorTab
          sign={sign}
          setActiveTab={setActiveTab}
          setToolboxHeight={setToolboxHeight}
          setSubTarget={setSubTarget}
        />
      )
    } else if (activeTab === "alignments") {
      return (
        <AlignmentTab
          activeObject={activeObject}
          data={data}
          sign={sign}
          setActiveTab={setActiveTab}
          setToolboxHeight={setToolboxHeight}
          updateObject={updateObject}
        />
      )
    } else if (activeTab === "border") {
      return <BorderTab sign={sign} setActiveTab={setActiveTab} setToolboxHeight={setToolboxHeight} />
    }

    return (
      <AllObjectsTab
        data={data}
        signState={signState}
        sign={sign}
        setActiveTab={setActiveTab}
        missingGlyphs={missingGlyphs}
        emojiUsed={emojiUsed}
        setToolbox={setToolbox}
        signType={signType}
        text={text}
      />
    )
  }

  return (
    <Toolbox className={cn(activeTab)} tKey="text" hideHeaderBar={activeTab != "all-objects"}>
      <div id="toolbox_text" className="object-toolbox text h-full">
        <div className="object-settings">{activeTabComponent()}</div>
      </div>
    </Toolbox>
  )
}

export default connect(
  ({ signType, signState, currentSignData, activeObject, subToolbox }: State): TextToolboxStateProps => ({
    signType,
    signState,
    activeObject,
    subToolbox,
    data: currentSignData,
  }),
  {
    setToolbox: actions.setToolbox,
    setSubToolbox: actions.setSubToolbox,
    setSubTarget: actions.setSubTarget,
    setToolboxHeight: actions.setToolboxHeight,
  }
)(TextToolbox)
